import React from 'react';
import { Text, Stack } from '@fluentui/react';
import { FiTrendingUp, FiTrendingDown, FiMinus } from 'react-icons/fi';
import './StaticWidgets.css';

/**
 * Widget de métrique statique
 */
export const StaticMetricWidget = ({ title, value, subtitle, trend, color = '#0078D4', icon: Icon }) => {
    const getTrendIcon = () => {
        if (trend > 0) return <FiTrendingUp className="trend-up" />;
        if (trend < 0) return <FiTrendingDown className="trend-down" />;
        return <FiMinus className="trend-neutral" />;
    };

    return (
        <div className="static-metric-widget" style={{ borderLeft: `4px solid ${color}` }}>
            <div className="metric-header">
                <Text variant="medium" className="metric-title">{title}</Text>
                {Icon && (
                    <div className="metric-icon" style={{ backgroundColor: `${color}15`, color }}>
                        <Icon size={20} />
                    </div>
                )}
            </div>
            
            <div className="metric-body">
                <Text variant="xxLarge" className="metric-value" style={{ color }}>
                    {value}
                </Text>
                
                {subtitle && (
                    <Text variant="small" className="metric-subtitle">{subtitle}</Text>
                )}
                
                {trend !== undefined && (
                    <div className="metric-trend">
                        {getTrendIcon()}
                        <Text variant="small">{Math.abs(trend)}%</Text>
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * Widget de graphique en barres statique
 */
export const StaticBarChartWidget = ({ title, data, color = '#0078D4' }) => {
    // Vérification de sécurité
    if (!data || !Array.isArray(data) || data.length === 0) {
        return (
            <div className="static-chart-widget">
                <Text variant="large" className="chart-title">{title || 'Bar Chart'}</Text>
                <div style={{ padding: '40px', textAlign: 'center', color: '#8a8886' }}>
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>📊</div>
                    <div>No data available</div>
                </div>
            </div>
        );
    }
    
    const maxValue = Math.max(...data.map(d => d.value));
    
    return (
        <div className="static-chart-widget">
            <Text variant="large" className="chart-title">{title}</Text>
            
            <div className="bar-chart-container">
                {data.map((item, index) => (
                    <div key={index} className="bar-item">
                        <div className="bar-label">
                            <Text variant="small">{item.label}</Text>
                        </div>
                        <div className="bar-wrapper">
                            <div 
                                className="bar-fill"
                                style={{ 
                                    width: `${(item.value / maxValue) * 100}%`,
                                    backgroundColor: color
                                }}
                            >
                                <span className="bar-value">{item.value}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

/**
 * Widget de graphique en donut statique
 */
export const StaticDonutChartWidget = ({ title, data }) => {
    // Vérification de sécurité
    if (!data || !Array.isArray(data) || data.length === 0) {
        return (
            <div className="static-chart-widget">
                <Text variant="large" className="chart-title">{title || 'Donut Chart'}</Text>
                <div style={{ padding: '40px', textAlign: 'center', color: '#8a8886' }}>
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>🍩</div>
                    <div>No data available</div>
                </div>
            </div>
        );
    }
    
    const total = data.reduce((sum, item) => sum + item.value, 0);
    let currentAngle = 0;

    const segments = data.map((item, index) => {
        const percentage = (item.value / total) * 100;
        const angle = (item.value / total) * 360;
        const startAngle = currentAngle;
        currentAngle += angle;

        return {
            ...item,
            percentage,
            angle,
            startAngle
        };
    });

    return (
        <div className="static-chart-widget">
            <Text variant="large" className="chart-title">{title}</Text>
            
            <div className="donut-chart-container">
                <div className="donut-chart">
                    <svg viewBox="0 0 100 100" className="donut-svg">
                        {segments.map((segment, index) => {
                            const radius = 35;
                            const innerRadius = 25;
                            const startAngleRad = (segment.startAngle - 90) * Math.PI / 180;
                            const endAngleRad = (segment.startAngle + segment.angle - 90) * Math.PI / 180;
                            
                            const x1 = 50 + radius * Math.cos(startAngleRad);
                            const y1 = 50 + radius * Math.sin(startAngleRad);
                            const x2 = 50 + radius * Math.cos(endAngleRad);
                            const y2 = 50 + radius * Math.sin(endAngleRad);
                            
                            const x3 = 50 + innerRadius * Math.cos(endAngleRad);
                            const y3 = 50 + innerRadius * Math.sin(endAngleRad);
                            const x4 = 50 + innerRadius * Math.cos(startAngleRad);
                            const y4 = 50 + innerRadius * Math.sin(startAngleRad);
                            
                            const largeArc = segment.angle > 180 ? 1 : 0;
                            
                            const pathData = `
                                M ${x1} ${y1}
                                A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}
                                L ${x3} ${y3}
                                A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4}
                                Z
                            `;
                            
                            return (
                                <path
                                    key={index}
                                    d={pathData}
                                    fill={segment.color}
                                    className="donut-segment"
                                />
                            );
                        })}
                    </svg>
                    <div className="donut-center">
                        <Text variant="xxLarge" className="donut-total">{total}</Text>
                        <Text variant="small" className="donut-label">Total</Text>
                    </div>
                </div>
                
                <div className="donut-legend">
                    {segments.map((segment, index) => (
                        <div key={index} className="legend-item">
                            <div 
                                className="legend-color" 
                                style={{ backgroundColor: segment.color }}
                            />
                            <Text variant="small" className="legend-label">{segment.label}</Text>
                            <Text variant="small" className="legend-value">
                                {segment.percentage % 1 === 0 ? segment.percentage.toFixed(0) : segment.percentage.toFixed(1)}%
                            </Text>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

/**
 * Widget de ligne temporelle statique
 */
export const StaticLineChartWidget = ({ title, data, color = '#0078D4' }) => {
    // Vérification de sécurité
    if (!data || !Array.isArray(data) || data.length === 0) {
        return (
            <div className="static-chart-widget">
                <Text variant="large" className="chart-title">{title || 'Line Chart'}</Text>
                <div style={{ padding: '40px', textAlign: 'center', color: '#8a8886' }}>
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>📈</div>
                    <div>No data available</div>
                </div>
            </div>
        );
    }
    
    const maxValue = Math.max(...data.map(d => d.value));
    const minValue = Math.min(...data.map(d => d.value));
    const range = maxValue - minValue || 1; // Éviter division par zéro
    
    const points = data.map((item, index) => {
        const x = (index / (data.length - 1)) * 100;
        const y = 100 - ((item.value - minValue) / range) * 80;
        return `${x},${y}`;
    }).join(' ');

    return (
        <div className="static-chart-widget">
            <Text variant="large" className="chart-title">{title}</Text>
            
            <div className="line-chart-container">
                <svg viewBox="0 0 100 100" className="line-chart-svg" preserveAspectRatio="none">
                    <polyline
                        points={points}
                        fill="none"
                        stroke={color}
                        strokeWidth="2"
                        className="line-path"
                    />
                    {data.map((item, index) => {
                        const x = (index / (data.length - 1)) * 100;
                        const y = 100 - ((item.value - minValue) / range) * 80;
                        return (
                            <circle
                                key={index}
                                cx={x}
                                cy={y}
                                r="3"
                                fill={color}
                                className="line-point"
                            />
                        );
                    })}
                </svg>
                
                <div className="line-chart-labels">
                    {data.map((item, index) => (
                        <div key={index} className="line-label">
                            <Text variant="tiny">{item.label}</Text>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

/**
 * Widget de jauge statique
 */
export const StaticGaugeWidget = ({ title, value, max, unit, color = '#0078D4', thresholds }) => {
    const percentage = (value / max) * 100;
    const angle = (percentage / 100) * 180 - 90;
    
    const getColor = () => {
        if (!thresholds) return color;
        if (value >= thresholds.good) return '#107C10';
        if (value >= thresholds.warning) return '#FF8C00';
        return '#D13438';
    };
    
    const gaugeColor = getColor();

    return (
        <div className="static-gauge-widget">
            <Text variant="large" className="gauge-title">{title}</Text>
            
            <div className="gauge-container">
                <svg viewBox="0 0 100 60" className="gauge-svg">
                    {/* Background arc */}
                    <path
                        d="M 10 50 A 40 40 0 0 1 90 50"
                        fill="none"
                        stroke="#edebe9"
                        strokeWidth="8"
                        strokeLinecap="round"
                    />
                    
                    {/* Value arc */}
                    <path
                        d="M 10 50 A 40 40 0 0 1 90 50"
                        fill="none"
                        stroke={gaugeColor}
                        strokeWidth="8"
                        strokeLinecap="round"
                        strokeDasharray={`${percentage * 1.25} 125`}
                        className="gauge-value-arc"
                    />
                    
                    {/* Needle */}
                    <line
                        x1="50"
                        y1="50"
                        x2={50 + 35 * Math.cos(angle * Math.PI / 180)}
                        y2={50 + 35 * Math.sin(angle * Math.PI / 180)}
                        stroke={gaugeColor}
                        strokeWidth="2"
                        strokeLinecap="round"
                    />
                    <circle cx="50" cy="50" r="3" fill={gaugeColor} />
                </svg>
                
                <div className="gauge-value-container">
                    <Text variant="xxLarge" className="gauge-value" style={{ color: gaugeColor }}>
                        {value}
                    </Text>
                    <Text variant="small" className="gauge-unit">{unit}</Text>
                    <Text variant="tiny" className="gauge-max">of {max}</Text>
                </div>
            </div>
        </div>
    );
};

/**
 * Widget de tableau statique
 */
export const StaticTableWidget = ({ title, headers, rows }) => {
    return (
        <div className="static-table-widget">
            <Text variant="large" className="table-title">{title}</Text>
            
            <div className="analytics-table-container">
                <table className="static-table">
                    <thead>
                        <tr>
                            {headers.map((header, index) => (
                                <th key={index}>{header}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                {row.map((cell, cellIndex) => (
                                    <td key={cellIndex}>{cell}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// Données d'exemple pour les widgets statiques
export const STATIC_WIDGET_DATA = {
    // Métriques de base
    'total-calls': {
        title: 'Total Calls',
        value: '1,247',
        subtitle: 'Last 24 hours',
        trend: 12.5,
        color: '#0078D4'
    },
    'calls-answered': {
        title: 'Answered Calls',
        value: '1,089',
        subtitle: '87.3% answer rate',
        trend: 5.2,
        color: '#107C10'
    },
    'calls-abandoned': {
        title: 'Abandoned Calls',
        value: '158',
        subtitle: '12.7% abandon rate',
        trend: -3.1,
        color: '#D13438'
    },
    'abandonment-rate': {
        title: 'Abandonment Rate',
        value: '12.7%',
        subtitle: 'Of total calls',
        trend: -3.1,
        color: '#D13438'
    },
    'avg-wait-time': {
        title: 'Avg Wait Time',
        value: '2m 34s',
        subtitle: 'Before answer',
        trend: -15.3,
        color: '#FF8C00'
    },
    'avg-handling-time': {
        title: 'Avg Handling Time',
        value: '5m 12s',
        subtitle: 'Including hold',
        trend: 2.1,
        color: '#0078D4'
    },
    'avg-talk-time': {
        title: 'Avg Talk Time',
        value: '4m 45s',
        subtitle: 'Active conversation',
        trend: 1.8,
        color: '#00BCF2'
    },
    'service-level': {
        title: 'Service Level',
        value: '92.5%',
        subtitle: 'Within 30s',
        trend: 4.2,
        color: '#107C10'
    },
    'fcr': {
        title: 'First Call Resolution',
        value: '87.3%',
        subtitle: 'Resolved on first contact',
        trend: 3.5,
        color: '#107C10'
    },
    'csat': {
        title: 'Customer Satisfaction',
        value: '4.6/5',
        subtitle: 'Based on 342 surveys',
        trend: 2.3,
        color: '#8764B8'
    },
    'widget-total-calls': {
        title: 'Total Calls',
        value: '1,247',
        subtitle: 'Last 24 hours',
        trend: 12.5,
        color: '#0078D4'
    },
    'widget-answered-calls': {
        title: 'Answered Calls',
        value: '1,089',
        subtitle: '87.3% answer rate',
        trend: 5.2,
        color: '#107C10'
    },
    'widget-abandoned-calls': {
        title: 'Abandoned Calls',
        value: '158',
        subtitle: '12.7% abandon rate',
        trend: -3.1,
        color: '#D13438'
    },
    'widget-outbound-calls': {
        title: 'Outbound Calls',
        value: '423',
        subtitle: 'Today',
        trend: 8.7,
        color: '#8764B8'
    },
    'widget-transferred-calls': {
        title: 'Transferred Calls',
        value: '234',
        subtitle: '18.8% of total',
        trend: 5.4,
        color: '#00BCF2'
    },
    'widget-average-wait-time': {
        title: 'Avg Wait Time',
        value: '2m 34s',
        subtitle: 'Before answer',
        trend: -15.3,
        color: '#FF8C00'
    },
    'widget-service-level': {
        title: 'Service Level',
        value: '92.5%',
        subtitle: 'Within 30s',
        trend: 4.2,
        color: '#00BCF2'
    },
    'widget-avg-inbound-call-duration': {
        title: 'Avg Inbound Duration',
        value: '5m 47s',
        subtitle: 'Per call',
        trend: 2.3,
        color: '#0078D4'
    },
    'widget-avg-outbound-call-duration': {
        title: 'Avg Outbound Duration',
        value: '4m 22s',
        subtitle: 'Per call',
        trend: -1.2,
        color: '#8764B8'
    },
    
    // Graphiques en barres
    'calls-by-hour': {
        title: 'Calls by Hour',
        data: [
            { label: '00h', value: 45 },
            { label: '02h', value: 32 },
            { label: '04h', value: 28 },
            { label: '06h', value: 67 },
            { label: '08h', value: 125 },
            { label: '10h', value: 156 },
            { label: '12h', value: 178 },
            { label: '14h', value: 165 },
            { label: '16h', value: 156 },
            { label: '18h', value: 112 },
            { label: '20h', value: 89 },
            { label: '22h', value: 67 }
        ],
        color: '#0078D4'
    },
    'widget-call-volume-hourly': {
        title: 'Hourly Call Volume',
        data: [
            { label: '00h', value: 45 },
            { label: '04h', value: 32 },
            { label: '08h', value: 125 },
            { label: '12h', value: 178 },
            { label: '16h', value: 156 },
            { label: '20h', value: 89 }
        ],
        color: '#0078D4'
    },
    'widget-call-volume-daily': {
        title: 'Daily Call Volume',
        data: [
            { label: 'Mon', value: 842 },
            { label: 'Tue', value: 923 },
            { label: 'Wed', value: 1089 },
            { label: 'Thu', value: 967 },
            { label: 'Fri', value: 1247 },
            { label: 'Sat', value: 456 },
            { label: 'Sun', value: 398 }
        ],
        color: '#0078D4'
    },
    'calls-by-day': {
        title: 'Calls by Day',
        data: [
            { label: 'Monday', value: 842 },
            { label: 'Tuesday', value: 923 },
            { label: 'Wednesday', value: 1089 },
            { label: 'Thursday', value: 967 },
            { label: 'Friday', value: 1247 },
            { label: 'Saturday', value: 456 },
            { label: 'Sunday', value: 398 }
        ],
        color: '#0078D4'
    },
    'calls-by-queue': {
        title: 'Calls by Queue',
        data: [
            { label: 'Support', value: 487 },
            { label: 'Sales', value: 356 },
            { label: 'Billing', value: 234 },
            { label: 'Technical', value: 170 }
        ],
        color: '#8764B8'
    },
    'widget-calls-by-queue': {
        title: 'Calls by Queue',
        data: [
            { label: 'Support', value: 487 },
            { label: 'Sales', value: 356 },
            { label: 'Billing', value: 234 },
            { label: 'Technical', value: 170 }
        ],
        color: '#8764B8'
    },
    'calls-by-agent': {
        title: 'Calls by Agent',
        data: [
            { label: 'Agent A', value: 156 },
            { label: 'Agent B', value: 142 },
            { label: 'Agent C', value: 138 },
            { label: 'Agent D', value: 125 },
            { label: 'Agent E', value: 118 }
        ],
        color: '#107C10'
    },
    'widget-calls-per-agent': {
        title: 'Calls per Agent',
        data: [
            { label: 'Agent A', value: 156 },
            { label: 'Agent B', value: 142 },
            { label: 'Agent C', value: 138 },
            { label: 'Agent D', value: 125 },
            { label: 'Agent E', value: 118 }
        ],
        color: '#107C10'
    },
    'widget-calls-by-flow': {
        title: 'Calls by Flow',
        data: [
            { label: 'Main', value: 567 },
            { label: 'Support', value: 345 },
            { label: 'Sales', value: 235 },
            { label: 'After Hours', value: 100 }
        ],
        color: '#00BCF2'
    },
    'wait-time-by-hour': {
        title: 'Wait Time by Hour',
        data: [
            { label: '00h', value: 45 },
            { label: '04h', value: 52 },
            { label: '08h', value: 185 },
            { label: '12h', value: 234 },
            { label: '16h', value: 198 },
            { label: '20h', value: 112 }
        ],
        color: '#FF8C00'
    },
    'widget-peak-hours': {
        title: 'Peak Hours Analysis',
        data: [
            { label: '08h-10h', value: 281 },
            { label: '10h-12h', value: 334 },
            { label: '12h-14h', value: 343 },
            { label: '14h-16h', value: 321 },
            { label: '16h-18h', value: 268 }
        ],
        color: '#D13438'
    },
    'widget-response-time-distribution': {
        title: 'Response Time Distribution',
        data: [
            { label: '0-30s', value: 456 },
            { label: '30s-1m', value: 342 },
            { label: '1m-2m', value: 234 },
            { label: '2m-5m', value: 156 },
            { label: '5m+', value: 59 }
        ],
        color: '#00BCF2'
    },
    'widget-talking-time-bar': {
        title: 'Talking Time Analysis',
        data: [
            { label: 'Current Avg', value: 180 },
            { label: 'Target', value: 150 },
            { label: 'Maximum', value: 300 }
        ],
        color: '#0078D4'
    },
    
    // Graphiques en donut/pie
    'calls-by-status': {
        title: 'Call Status Distribution',
        data: [
            { label: 'Answered', value: 1089, color: '#107C10' },
            { label: 'Abandoned', value: 158, color: '#D13438' },
            { label: 'Transferred', value: 234, color: '#0078D4' },
            { label: 'Voicemail', value: 89, color: '#8764B8' }
        ]
    },
    'widget-call-status-distribution': {
        title: 'Call Status Distribution',
        data: [
            { label: 'Answered', value: 1089, color: '#107C10' },
            { label: 'Abandoned', value: 158, color: '#D13438' },
            { label: 'Transferred', value: 234, color: '#0078D4' },
            { label: 'Voicemail', value: 89, color: '#8764B8' }
        ]
    },
    'widget-abandon-time-donut': {
        title: 'Abandon Time Distribution',
        data: [
            { label: 'Quick Drop (< 7s)', value: 15, color: '#0078D4' },
            { label: 'Before Target (7-30s)', value: 50, color: '#107C10' },
            { label: 'Before Max (30-60s)', value: 25, color: '#FF8C00' },
            { label: 'Exceeds Max (> 60s)', value: 10, color: '#D13438' }
        ]
    },
    
    // Graphiques gauge
    'widget-waiting-time-gauge': {
        title: 'Average Waiting Time',
        value: 154,
        max: 300,
        unit: 'seconds',
        color: '#0078D4',
        thresholds: { good: 200, warning: 150 }
    },
    'queue-performance': {
        title: 'Queue Performance',
        value: 87,
        max: 100,
        unit: '%',
        color: '#107C10',
        thresholds: { good: 80, warning: 60 }
    },
    'agent-performance': {
        title: 'Agent Performance',
        value: 92,
        max: 100,
        unit: '%',
        color: '#107C10',
        thresholds: { good: 85, warning: 70 }
    },
    
    // Sparkline widgets
    'widget-sparkline': {
        title: 'Call Trend',
        data: [
            { x: 1, y: 45 },
            { x: 2, y: 52 },
            { x: 3, y: 48 },
            { x: 4, y: 65 },
            { x: 5, y: 72 },
            { x: 6, y: 68 },
            { x: 7, y: 85 },
            { x: 8, y: 92 },
            { x: 9, y: 88 },
            { x: 10, y: 95 }
        ],
        color: '#0078D4'
    },
    
    // Area chart widgets
    'widget-area-chart': {
        title: 'Volume Over Time',
        data: [
            { x: 1, y: 120 },
            { x: 2, y: 145 },
            { x: 3, y: 138 },
            { x: 4, y: 165 },
            { x: 5, y: 182 },
            { x: 6, y: 195 }
        ],
        color: '#00BCF2'
    },
    
    // Scatter plot widgets
    'widget-scatter': {
        title: 'Call Duration vs Wait Time',
        data: [
            { label: 'A', value: 45 },
            { label: 'B', value: 62 },
            { label: 'C', value: 78 },
            { label: 'D', value: 52 },
            { label: 'E', value: 88 }
        ],
        color: '#8764B8'
    },
    
    // Funnel widgets
    'widget-funnel': {
        title: 'Call Resolution Funnel',
        data: [
            { label: 'Total Calls', value: 1000 },
            { label: 'Answered', value: 850 },
            { label: 'Handled', value: 720 },
            { label: 'Resolved', value: 650 }
        ],
        color: '#107C10'
    },
    
    // Heatmap widgets
    'widget-heatmap': {
        title: 'Hourly Call Heatmap',
        data: [
            { label: 'Mon', value: 234 },
            { label: 'Tue', value: 267 },
            { label: 'Wed', value: 289 },
            { label: 'Thu', value: 256 },
            { label: 'Fri', value: 245 }
        ],
        color: '#FF8C00'
    },
    
    // Progress bar widgets
    'widget-progress-bar': {
        title: 'Daily Target Progress',
        data: [
            { label: 'Completed', value: 75 },
            { label: 'Remaining', value: 25 }
        ],
        color: '#0078D4'
    },
    
    // Pie chart widgets
    'widget-pie-chart': {
        title: 'Call Distribution',
        data: [
            { label: 'Inbound', value: 650, color: '#0078D4' },
            { label: 'Outbound', value: 250, color: '#107C10' },
            { label: 'Internal', value: 100, color: '#8764B8' }
        ]
    },
    
    // Grouped bar widgets
    'widget-grouped-bar': {
        title: 'Team Comparison',
        data: [
            { label: 'Team A', value: 145 },
            { label: 'Team B', value: 167 },
            { label: 'Team C', value: 152 },
            { label: 'Team D', value: 189 }
        ],
        color: '#D13438'
    }
};
